import { Link } from "react-router-dom";
import { FormattedNumber, useIntl } from "react-intl";
import moment from "moment";
import { IReservedOrderDetail } from "@models/DTO/IOnGetReservedOrdersDTO";
import OwLabel from "@components/shared/OwLabel/OwLabel";

type Props = {
    order: IReservedOrderDetail;
    closeFn: () => void;
};

/**
 * One row of the Reserved Qty On Hand drill-down. An order-line reservation shows its order; a BOM
 * component row (OR-1497) also shows the finished SKU and serial holding it, and, when that serial is
 * not on an order yet, whether it is still in production.
 */
export const ReservedOrderRow = ({ order, closeFn }: Props) => {
    const intl = useIntl();
    const isBom = order.source === "bom";
    const hasOrder = !!order.orderGuid;

    return (
        <tr data-testid="reserved-order-row">
            <td>
                {hasOrder ? (
                    <Link to={`/orders/${order.orderGuid}/entry`} className="text-primary" onClick={closeFn}>
                        {order.orderNo}
                    </Link>
                ) : (
                    <span className="text-muted fst-italic">
                        {order.inProduction
                            ? intl.formatMessage({ id: "in_production", defaultMessage: "In Production" })
                            : intl.formatMessage({ id: "not_assigned_to_order", defaultMessage: "Not assigned to an order" })}
                    </span>
                )}
                {isBom ? (
                    <div className="bom-source small text-muted" data-testid="reserved-order-bom-source">
                        {intl.formatMessage(
                            { id: "bom_reserved_serial_for_sku", defaultMessage: "{sku} · Serial {serial}" },
                            { sku: order.finishedSku || "", serial: order.serialValue || "" }
                        )}
                    </div>
                ) : null}
            </td>
            <td>{order.orderDate ? moment(order.orderDate).format("MM/DD/YYYY") : ""}</td>
            <td>{order.customerName}</td>
            <td className="text-end">
                <FormattedNumber value={order.qtyReserved} minimumFractionDigits={0} />
            </td>
            <td>{order.orderState ? <OwLabel title={order.orderState.toLowerCase()} /> : null}</td>
        </tr>
    );
};

export default ReservedOrderRow;
